import { Link, usePathname, useRouter } from '@/router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { call } from '@edil/api-client';
import { Avatar, Badge, IconBell, IconChevronDown, IconLogOut, IconUser, IconWallet, IconWifiOff, Logo, buttonClass, cx } from '@edil/ui';
import { useApp } from '@/components/app-provider';
import { money } from '@/lib/format';
import { ExclusionBanner, RealityCheck } from '@/components/protection';
import { NAV_SECTIONS, isActive, visibleItems } from './navigation';
import s from './AppShell.module.css';
import { PhoneGate } from '@/components/phone-gate';

/** Application frame for every signed-in or browsing screen. */
export function AppShell({ children }: { children: ReactNode }) {
  const { t, live, ready, signedIn, wallet, currency, telegram, bingoEnabled } = useApp();
  const path = usePathname();
  const items = visibleItems({ currency, signedIn, features: { bingo: bingoEnabled } });
  const balance = wallet ? (currency === 'ETB' ? wallet.etb.balance : wallet.points.balance) : null;
  // Only report a lost connection after a grace period, not during the first connect.
  const [offline, setOffline] = useState(false);
  useEffect(() => {
    if (live || !ready) return setOffline(false);
    const id = setTimeout(() => setOffline(true), 3000);
    return () => clearTimeout(id);
  }, [live, ready]);

  return (
    <div className={s.root}>
      <aside className={s.sidebar} aria-label={t('nav.main')}>
        <Link href="/" className={s.brand}>
          <Logo size={34} />
        </Link>
        {NAV_SECTIONS.map((section) => {
          const sectionItems = items.filter((i) => i.section === section.id);
          if (!sectionItems.length) return null;
          return (
            <nav key={section.id} className={s.section} aria-label={t(section.label)}>
              <span className={s.sectionLabel}>{t(section.label)}</span>
              {sectionItems.map((item) => (
                <Link key={item.id} href={item.href} className={cx(s.navLink, isActive(item, path) && s.active)} aria-current={isActive(item, path) ? 'page' : undefined}>
                  <item.icon size={18} />
                  {t(item.label)}
                </Link>
              ))}
            </nav>
          );
        })}
        <div className={s.sidebarFoot}>
          <span>{t('rg.footer')}</span>
        </div>
      </aside>

      <div className={s.main}>
        <header className={s.topbar}>
          <Link href="/" className={s.mobileBrand}>
            <Logo size={30} />
          </Link>
          <span className={s.topbarSpacer} />
          {ready && (
            <span className={s.status}>
              <Badge tone={offline ? 'warning' : 'success'} dot pulse={!offline}>
                {offline ? t('status.reconnecting') : t('status.live')}
              </Badge>
            </span>
          )}
          {signedIn && balance !== null && (
            <Link href="/wallet/" className={s.balance} title={t('nav.wallet')}>
              <IconWallet size={16} />
              {money(balance, currency)}
            </Link>
          )}
          {signedIn && <NotificationBell />}
          {signedIn ? (
            <AccountMenu />
          ) : (
            ready &&
            !telegram && (
              <Link href="/login/" className={buttonClass('primary', 'sm')}>
                {t('auth.sign_in')}
              </Link>
            )
          )}
        </header>
        {offline && (
          <div className={s.offline} role="status">
            <IconWifiOff size={14} /> {t('common.live_paused')}
          </div>
        )}
        <main className={s.content}>
          <ExclusionBanner />
          <PhoneGate />
          {children}
        </main>
        <RealityCheck />
        <footer className={s.footer}>{t('rg.footer')}</footer>
      </div>

      <nav className={s.bottomnav} aria-label={t('nav.main')}>
        {items
          .filter((i) => i.primary)
          .map((item) => {
            const active = isActive(item, path);
            return (
              <Link key={item.id} href={item.href} className={cx(s.tab, active && s.active)} aria-current={active ? 'page' : undefined}>
                <span className={s.tabIcon}>
                  <item.icon size={20} />
                </span>
                {t(item.label)}
              </Link>
            );
          })}
      </nav>
    </div>
  );
}

function AccountMenu() {
  const { me, t, signOut } = useApp();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const name = me?.display_name ?? '';
  return (
    <div className={s.account} ref={ref}>
      <button className={s.accountButton} onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu" aria-label={t('nav.account')}>
        <Avatar name={name || '·'} size={34} />
        <IconChevronDown size={16} />
      </button>
      {open && (
        <div className={s.menu} role="menu">
          <div className={s.menuHead}>
            <div className={s.menuName}>{name}</div>
            <div className={s.menuSub}>{me?.phone}</div>
          </div>
          <button
            className={s.menuItem}
            role="menuitem"
            onClick={() => {
              setOpen(false);
              router.push('/profile/');
            }}
          >
            <IconUser size={16} /> {t('nav.profile')}
          </button>
          <button
            className={s.menuItem}
            role="menuitem"
            data-testid="sign-out"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            <IconLogOut size={16} /> {t('auth.sign_out')}
          </button>
        </div>
      )}
    </div>
  );
}

/** Unread count, refreshed on the player's realtime events and after reading. */
function NotificationBell() {
  const { api, t, me, realtime } = useApp();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!me) return;
    const load = () =>
      call(api.GET('/v1/me/notifications', { params: { query: { limit: 1 } } }))
        .then((r) => setUnread(r.unread))
        .catch(() => {});
    void load();
    window.addEventListener('edil:inbox-changed', load);
    // The worker writes the inbox copy just after the event is pushed; give it a moment.
    const off = realtime.subscribe(`user:${me.id}`, () => void setTimeout(load, 1500));
    return () => {
      off();
      window.removeEventListener('edil:inbox-changed', load);
    };
  }, [api, me?.id, realtime]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Link href="/notifications/" className={s.bell} title={t('notifications.open')} data-testid="bell">
      <IconBell size={18} />
      {unread > 0 && <span className={s.bellCount}>{unread > 9 ? '9+' : unread}</span>}
    </Link>
  );
}
